import { useEffect, useMemo, useState } from "react";
import CrudPage from "@/components/CrudPage";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Music, RefreshCw } from "lucide-react";

export default function MusicOnHold() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";
  const [rendering, setRendering] = useState(false);
  const [result, setResult] = useState("");
  const [tenants, setTenants] = useState([]);

  useEffect(() => {
    api.get("/tenants")
      .then(({ data }) => setTenants(data || []))
      .catch(() => setTenants([]));
  }, []);

  const tenantMap = useMemo(
    () => Object.fromEntries(tenants.map((tenant) => [tenant.id, tenant.nome])),
    [tenants],
  );
  const tenantOptions = useMemo(
    () => [
      { value: "", label: "Selecione o cliente" },
      ...tenants.filter((tenant) => tenant.enabled !== false).map((tenant) => ({ value: tenant.id, label: tenant.nome })),
    ],
    [tenants],
  );
  const fields = useMemo(() => [
    ...(isSuperAdmin ? [{
      key: "tenant_id",
      label: "Cliente",
      type: "select",
      full: true,
      options: tenantOptions,
    }] : []),
    { key: "nome", label: "Nome", placeholder: "Ex.: Espera institucional" },
    { key: "classe", label: "Classe MOH", mono: true, placeholder: "default", hint: "Nome amigavel da classe dentro deste cliente." },
    {
      key: "audio_path",
      label: "Audio",
      full: true,
      type: "file",
      accept: ".wav,.wave,.mp3",
      uploadEndpoint: "/moh/upload-audio",
      uploadParams: (form) => ({ tenant_id: form.tenant_id || user?.tenant_id || "" }),
      targetKey: "audio_path",
    },
    {
      key: "is_default",
      label: "Espera padrao do cliente",
      type: "toggle",
      full: true,
      hint: "Usada automaticamente pelos ramais e filas deste cliente quando nao houver outra musica selecionada.",
    },
    { key: "descricao", label: "Descricao", full: true, type: "textarea" },
    { key: "enabled", label: "Ativo", type: "toggle" },
  ], [isSuperAdmin, tenantOptions, user?.tenant_id]);

  const render = async () => {
    setRendering(true);
    setResult("");
    try {
      const { data } = await api.post("/moh/render");
      setResult(`${data.classes} classe(s) gerada(s) em ${data.path}`);
    } finally {
      setRendering(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button className="sbc-btn sbc-btn-primary" onClick={render} disabled={rendering}>
          {rendering ? <RefreshCw size={14} className="animate-spin" /> : <Music size={14} />} Aplicar MOH
        </button>
      </div>
      {result && <div className="text-xs font-mono text-[color:var(--accent-green)]">{result}</div>}
      <CrudPage
        title="Musica em Espera"
        subtitle="Espera por cliente"
        endpoint="/moh"
        testidPrefix="moh"
        fields={fields}
        headers={[...(isSuperAdmin ? ["Cliente"] : []), "Classe", "Nome", "Audio", "Padrao", "Status"]}
        transformSubmit={(form) => ({
          ...form,
          tenant_id: isSuperAdmin ? form.tenant_id : (user?.tenant_id || form.tenant_id),
        })}
        defaultValues={{
          tenant_id: user?.tenant_id || "",
          nome: "",
          classe: "default",
          audio_path: "",
          descricao: "",
          is_default: false,
          enabled: true,
        }}
        renderRow={(row) => <>
          {isSuperAdmin && <td>{tenantMap[row.tenant_id] || row.tenant_id || "-"}</td>}
          <td className="font-mono">{row.classe}</td>
          <td>{row.nome}</td>
          <td className="font-mono text-xs break-all">{row.audio_path || "-"}</td>
          <td>{row.is_default ? <span className="sbc-tag tag-online">Padrao do cliente</span> : <span className="text-xs text-[color:var(--text-muted)]">Alternativa</span>}</td>
          <td>{row.enabled ? "Ativo" : "Inativo"}</td>
        </>}
      />
    </div>
  );
}
